import React, { useState } from "react";
import { PlusIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components";
import type { UserStory, AcceptanceCriteria } from "@/features/Projects";
import type { StoryEditFormProps } from "./storyEditForm.types";
import { createNewAcceptanceCriteria } from "../../utils/storyHelpers";

export default function StoryEditForm({ story, epics, onSave, onCancel }: StoryEditFormProps) {
  // Garantir que acceptanceCriteria sempre seja um array
  const [editedStory, setEditedStory] = useState<UserStory>({
    ...story,
    acceptanceCriteria: story.acceptanceCriteria || []
  });

  const handleInputChange = (field: keyof UserStory, value: any) => {
    setEditedStory(prev => ({ ...prev, [field]: value }));
  };

  const handleCriteriaChange = (index: number, description: string) => {
    const currentCriteria = editedStory.acceptanceCriteria || [];
    const updatedCriteria = [...currentCriteria];
    updatedCriteria[index] = { ...updatedCriteria[index], description };
    setEditedStory(prev => ({ ...prev, acceptanceCriteria: updatedCriteria }));
  };

  const addCriteria = () => {
    const newCriteria = createNewAcceptanceCriteria();
    setEditedStory(prev => ({
      ...prev,
      acceptanceCriteria: [...(prev.acceptanceCriteria || []), newCriteria]
    }));
  };

  const removeCriteria = (index: number) => {
    setEditedStory(prev => ({
      ...prev,
      acceptanceCriteria: (prev.acceptanceCriteria || []).filter((_, i) => i !== index)
    }));
  };

  const handleSave = () => {
    const updatedStory = {
      ...editedStory,
      updatedAt: new Date().toISOString(),
    };
    onSave(updatedStory);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
          Editando Story
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Título da Story
          </label>
          <input
            type="text"
            value={editedStory.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Título da user story..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Papel do Usuário
          </label>
          <input
            type="text"
            value={editedStory.role}
            onChange={(e) => handleInputChange('role', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="usuário, admin, cliente..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prioridade
          </label>
          <select
            value={editedStory.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="must_have">Must Have - Crítico</option>
            <option value="should_have">Should Have - Importante</option>
            <option value="could_have">Could Have - Desejável</option>
            <option value="wont_have">Won't Have - Não será feito</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo
          </label>
          <select
            value={editedStory.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="feature">Feature</option>
            <option value="enhancement">Melhoria</option>
            <option value="bug">Bug</option>
            <option value="technical">Técnica</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Story Points
          </label>
          <select
            value={editedStory.storyPoints || ''}
            onChange={(e) => handleInputChange('storyPoints', e.target.value ? parseInt(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Não estimado</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="5">5</option>
            <option value="8">8</option>
            <option value="13">13</option>
            <option value="21">21</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Épico
          </label>
          <select
            value={editedStory.epic || ''}
            onChange={(e) => handleInputChange('epic', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Sem épico</option>
            {epics.map(epic => (
              <option key={epic.id} value={epic.id}>{epic.title}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Funcionalidade
        </label>
        <input
          type="text"
          value={editedStory.functionality}
          onChange={(e) => handleInputChange('functionality', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="O que o usuário quer fazer..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Benefício
        </label>
        <input
          type="text"
          value={editedStory.benefit}
          onChange={(e) => handleInputChange('benefit', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="Por que é importante..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Descrição Completa
        </label>
        <textarea
          value={editedStory.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          rows={3}
          placeholder="Como [papel], eu quero [funcionalidade], para que [benefício]"
        />
      </div>

      {/* Critérios de Aceitação */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Critérios de Aceitação
          </label>
          <Button
            onClick={addCriteria}
            variant="secondary"
            size="sm"
            leftIcon={<PlusIcon className="h-4 w-4" />}
          >
            Adicionar Critério
          </Button>
        </div>
        <div className="space-y-2">
          {(editedStory.acceptanceCriteria || []).map((criteria, index) => (
            <div key={criteria.id} className="flex items-center space-x-2">
              <input
                type="text"
                value={criteria.description}
                onChange={(e) => handleCriteriaChange(index, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Dado que... Quando... Então..."
              />
              <Button
                onClick={() => removeCriteria(index)}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700"
                leftIcon={<TrashIcon className="h-4 w-4" />}
              >
                Remover
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button
          onClick={onCancel}
          variant="secondary"
          size="sm"
          leftIcon={<XMarkIcon className="h-4 w-4" />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          size="sm"
          leftIcon={<CheckIcon className="h-4 w-4" />}
        >
          Salvar Story
        </Button>
      </div>
    </div>
  );
}